import { useEffect, useState } from 'react'
import { Modal } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { Controlled as ControlledZoom } from 'react-medium-image-zoom'
import classNames from 'classnames'
import 'react-medium-image-zoom/dist/styles.css'

import { ERoutePaths } from 'libraries/router/types'
import { useResponsive } from 'hooks'
import { ProductSelectors } from 'store/product/selectors'
import { getProductDetails, getProductOptions } from 'store/product/actions'
import { BasketIcon, CloseIcon, HearthIcon, WishListIcon } from 'assets'
import { useAppDispatch, useAppSelector } from 'libraries/redux'
import { Breadcrumb, ProductSlider, Tabs } from 'components'
import { addBasket, addWishlist, deleteWishlist } from 'store/wishlist/actions'

import Sizes from './Sizes'
import styles from './Product.module.scss'
import Gallery from './Gallery'
import DeliveryAndPayment from './DeliveryAndPayment'
import Skeleton from '../../components/atom/Skeleton'
import { CategoriesSelectors } from 'store/category/selectors'
import { Helmet } from 'react-helmet'

const Product = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isTabletL } = useResponsive()

  const { id } = useParams()
  const { data, loading: loadingDetails } = useAppSelector(ProductSelectors.productDetails)
  const { data: options } = useAppSelector(ProductSelectors.productOptons)
  const { data: productList, loading } = useAppSelector(ProductSelectors.productList)

  const [isZoomed, setIsZoomed] = useState(false)
  const [activeSize, setActiveSize] = useState(null)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [productData, setProductData] = useState<any>(null)
  const [isOpeenSizeModal, setIsOpenSizeModal] = useState(false)

  const isWishlist = Boolean(productData?.in_wishlist)
  const [isWishlistActive, setIsWishlistActive] = useState(isWishlist)

  const warehouses = productData?.options?.find(({ warehouses }: any) => warehouses)?.warehouses

  const { data: storeInfo } = useAppSelector(CategoriesSelectors.storeInfo)

  const addWishListHandler = (product_id: any) => () => {
    if (isWishlistActive) {
      try {
        dispatch(deleteWishlist(product_id))
        setIsWishlistActive(false)
      } catch (error) {
        console.log(error)
        throw error
      }
    } else {
      try {
        dispatch(addWishlist(product_id))
        setIsWishlistActive(true)
        if (storeInfo?.fb_pixel_id) {
          ;(window as any).fbq('track', 'AddToWishlist', {
            content_ids: [product_id],
            content_type: 'product',
            currency: 'UAH',
          })
        }
        if (storeInfo?.tt_pixel_id) {
          ;(window as any).ttq.track('AddToWishlist', {
            content_id: product_id,
            content_type: 'product',
            currency: 'UAH',
            value: productData?.price,
            content_name: productData?.name,
          })
        }
      } catch (error) {
        console.log(error)
        throw error
      }
    }
  }

  const getProductInfo = () => {
    dispatch(getProductDetails(id))
  }

  const getProductOptionsInfo = () => {
    dispatch(getProductOptions(id))
  }

  const updateActiveSize = (size: any) => {
    setActiveSize(size)
  }

  const handleZoomChange = () => {
    setIsZoomed(false)
  }

  const addToBasket = () => {
    if (!activeSize) {
      setIsOpenSizeModal(true)

      return null
    }

    const allOptions = warehouses?.map((warehouse: any) => warehouse.options).flat()
    const activeOption = allOptions?.find(({ product_option_value_id }: any) => product_option_value_id === activeSize)

    const data: any = {
      product_id: Number(id),
      product_option_values: [
        {
          product_option_value_id: activeOption.product_option_value_id,
          product_option_id: activeOption.product_option_id,
          quantity: 1,
          warehouse_id: activeOption.warehouse_id,
        },
      ],
    }
    setIsOpenSizeModal(false)

    try {
      dispatch(
        addBasket({
          body: data,
          rejectValue: undefined,
        })
      )

      if (window.fbq && storeInfo?.fb_pixel_id) {
        window.fbq('track', 'AddToCart', {
          content_ids: [id],
          content_type: 'product',
          content_name: productData?.name,
          value: activeOption.name,
          currency: 'UAH',
        })
      }
      if (window.ttq && storeInfo?.tt_pixel_id) {
        window.ttq.track('AddToCart', {
          content_id: id,
          content_type: 'product',
          content_name: productData?.name,
          value: productData?.price,
          currency: 'UAH',
        })
      }
      setIsOpenModal(true)
    } catch (error) {
      console.log(error)
    }
  }

  const routesList = [
    {
      label: 'Каталог',
      link: ERoutePaths.Category,
    },
    {
      label: data?.name || '',
    },
  ]

  const renderTabs = [
    {
      id: 0,
      text: 'Деталі',
      ContentComponents: (
        <div className={styles.wrapper__details} dangerouslySetInnerHTML={{ __html: data?.description || '' }} />
      ),
    },
    {
      id: 1,
      text: 'Доставка та оплата',
      ContentComponents: <DeliveryAndPayment />,
    },
  ]

  const pricePercent = (
    (1 -
      parseFloat(String(data?.special)?.replace(/\s/g, '').replace('грн.', '')) /
        parseFloat(String(data?.price)?.replace(/\s/g, '').replace('грн.', ''))) *
    100
  ).toFixed(0)

  useEffect(() => {
    if (data && !productData) {
      setProductData(data)
    }
  }, [data])

  useEffect(() => {
    setProductData(null)
  }, [])

  useEffect(() => {
    getProductInfo()
    getProductOptionsInfo()
    setProductData(null)
  }, [id])

  useEffect(() => {
    setIsWishlistActive(isWishlist)
  }, [isWishlist])

  return (
    <>
      {data && (
        <>
          <Helmet>
            <title>{data.name}</title>
            <meta property='og:title' content={data.name} />
            <meta property='og:type' content='product' />
            <meta property='og:url' content={window.location.href} />
            <meta property='og:image' content={data.images?.[0]?.original || ''} />
            <meta property='product:price:amount' content={data.price} />
            <meta property='product:price:currency' content='UAH' />
            <meta property='og:description' content={data.description?.replace(/<[^>]+>/g, '').slice(0, 150) || ''} />

            <script type='application/ld+json'>
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: data.name,
                description: data.description?.replace(/<[^>]+>/g, '').slice(0, 150) || '',
                sku: data.model || String(data.product_id),
                url: window.location.href,
                image: data.images?.[0]?.original || '',
                brand: {
                  '@type': 'Brand',
                  name: data.manufacturer || 'Brand',
                },
                offers: {
                  '@type': 'Offer',
                  price: data.price,
                  priceCurrency: 'UAH',
                  itemCondition: 'https://schema.org/NewCondition',
                  availability: data.in_stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                  priceValidUntil: '2025-12-31',
                },
                additionalProperty: [
                  {
                    '@type': 'PropertyValue',
                    propertyID: 'item_group_id',
                    value: data.product_id,
                  },
                ],
              })}
            </script>
          </Helmet>
          <div itemScope itemType='https://schema.org/Product' style={{ display: 'none' }}>
            <div itemScope itemType='https://schema.org/Product' style={{ display: 'none' }}>
              <meta itemProp='brand' content={data?.manufacturer || 'Brand'} />
              <meta itemProp='name' content={data?.name} />
              <meta itemProp='description' content={data?.description?.replace(/<[^>]+>/g, '').slice(0, 150) || ''} />
              <meta itemProp='sku' content={data?.model || String(data?.product_id)} />
              <meta itemProp='url' content={window.location.href} />
              <meta itemProp='image' content={data?.images?.[0]?.original || ''} />

              <div itemProp='value' itemScope itemType='https://schema.org/PropertyValue'>
                <meta itemProp='propertyID' content='item_group_id' />
                <meta itemProp='value' content={String(data?.product_id)} />
              </div>

              <div itemProp='offers' itemScope itemType='https://schema.org/Offer'>
                <link
                  itemProp='availability'
                  href={data?.in_stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'}
                />
                <link itemProp='itemCondition' href='https://schema.org/NewCondition' />
                <meta itemProp='price' content={String(data?.price)} />
                <meta itemProp='priceCurrency' content='UAH' />
              </div>
            </div>
          </div>
        </>
      )}

      <section>
        <div className='container'>
          <div className={styles.divider}>
            <Breadcrumb routes={routesList} />
          </div>
          <div className={styles.wrapper}>
            {isTabletL && (
              <div>
                {loadingDetails ? (
                  <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                    <Skeleton width={'100%'} height={'20px'} />
                    <Skeleton width={'100%'} height={'20px'} />
                    <Skeleton width={'100%'} height={'20px'} />
                    <Skeleton width={'100%'} height={'20px'} />
                  </div>
                ) : (
                  <h1 className={styles.info__title}>{data?.name}</h1>
                )}

                <p className={styles.info__price}>
                  {loadingDetails ? (
                    <div style={{ marginTop: 20, width: '100%' }}>
                      <Skeleton width={'120px'} height={'20px'} />
                    </div>
                  ) : data?.special ? (
                    <>
                      ₴{data?.special}
                      <span className={styles.info__price__save}>₴{data?.price}</span>
                      <span className={styles.info__price_save_count}>Заощаджуй {pricePercent}%</span>
                    </>
                  ) : (
                    <>₴{data?.price}</>
                  )}
                </p>
              </div>
            )}

            <Gallery loading={loadingDetails} data={data} productData={productData} />
            <div className={styles.info}>
              {!isTabletL && (
                <>
                  {loadingDetails ? (
                    <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                      <Skeleton width={'94%'} height={'40px'} />
                      <Skeleton width={'100%'} height={'38px'} />
                      <Skeleton width={'74%'} height={'38px'} />
                    </div>
                  ) : (
                    <h1 className={styles.info__title}>{data?.name}</h1>
                  )}
                  <p className={styles.info__price}>
                    {loadingDetails ? (
                      <div style={{ marginTop: 12, width: '100%' }}>
                        <Skeleton width={'120px'} height={'20px'} />
                      </div>
                    ) : data?.special ? (
                      <>
                        ₴{data?.special}
                        <span className={styles.info__price__save}>₴{data?.price}</span>
                        <span className={styles.info__price_save_count}>Заощаджуй {pricePercent}%</span>
                      </>
                    ) : (
                      <>₴{data?.price}</>
                    )}
                  </p>
                </>
              )}
              <div className={styles.info__code}>Код: {data.model}</div>

              {loadingDetails ? (
                <div style={{ width: '100%', marginBottom: 20 }}>
                  <Skeleton width={'120px'} height={'20px'} />
                </div>
              ) : (
                data?.table_size && (
                  <div>
                    <p className={styles.info__sizes_image} onClick={() => setIsZoomed(true)}>
                      Таблиця розмірів
                    </p>

                    <div
                      className={classNames(styles.info__sizes_wrapper, {
                        [styles.info__sizes_wrapper_active]: isZoomed,
                      })}
                    >
                      <ControlledZoom isZoomed={isZoomed} onZoomChange={handleZoomChange}>
                        <img alt='That Wanaka Tree, New Zealand by Laura Smetsers' src={data?.table_size} />
                      </ControlledZoom>
                    </div>
                  </div>
                )
              )}

              {loadingDetails ? (
                <Skeleton width={'100%'} height={'62px'} />
              ) : (
                <Sizes options={options} activeSize={activeSize} updateActiveSize={updateActiveSize} />
              )}
              <div className={styles.controls}>
                <button className={styles.controls__basket} onClick={addToBasket}>
                  Додати в кошик ({data?.special ?? data?.price})
                </button>
                <button onClick={addWishListHandler(data?.product_id)} className={styles.controls__wishlist}>
                  {isWishlistActive ? <WishListIcon /> : <HearthIcon />}
                </button>
              </div>
              <div>
                {loadingDetails ? (
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Skeleton width='100%' height={'50px'} />
                    <Skeleton width='100%' height={'350px'} />
                  </div>
                ) : (
                  <Tabs tabs={renderTabs} />
                )}
              </div>
            </div>
          </div>

          <ProductSlider productList={productList} loading={loading} />
        </div>

        <Modal
          open={isOpeenSizeModal}
          onClose={() => {
            setIsOpenSizeModal(false)
          }}
        >
          <div className={styles.modal}>
            <div className={styles.modal__content}>
              <CloseIcon className={styles.modal__close} onClick={() => setIsOpenSizeModal(false)} />
              <p className={styles.modal__title}>Виберіть, будь ласка, розмір</p>
              {loadingDetails ? (
                <Skeleton width={'100%'} height={'62px'} />
              ) : (
                <Sizes options={options} activeSize={activeSize} updateActiveSize={updateActiveSize} />
              )}

              <div className={styles.modal__controls}>
                <button onClick={() => setIsOpenSizeModal(false)}>Скасувати</button>
                <button disabled={!activeSize} onClick={addToBasket}>
                  Підтвердити
                </button>
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          open={isOpenModal}
          onClose={() => {
            setIsOpenModal(false)
          }}
        >
          <div className={styles.modal}>
            <div className={styles.modal__content}>
              <CloseIcon className={styles.modal__close} onClick={() => setIsOpenModal(false)} />
              <div className={styles.modal__content__header}>
                <BasketIcon />
                <p>Товар успішно додано у ваш кошик</p>
              </div>
              <button onClick={() => navigate(ERoutePaths.Basket)}>Перейти у кошик</button>
              <button onClick={() => setIsOpenModal(false)}>Продовжити покупки</button>
            </div>
          </div>
        </Modal>
      </section>
    </>
  )
}

export default Product
